/**
 * Auto-Normalizer
 *
 * Walks an API response tree, discovers entity objects via their `__type` field,
 * formats them using the registered formatter, flattens nested entities into a
 * shared `entities` bucket, and returns `{ entities, result }` — the same shape
 * the old normalizr-based normalizers produced, but without the normalizr library.
 *
 * Usage:
 *   const { entities, result } = autoNormalize(apiResponse);
 */

import { AnyObject } from "../models";
import { typeRegistry, storeRegistry, FlattenConfig, TypeRegistryEntry } from "./typeRegistry";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EntitiesMap = Record<string, Record<string, AnyObject>>;

export type AutoNormalizeResult<R = unknown> = {
  entities?: EntitiesMap;
  result: R;
};

// ---------------------------------------------------------------------------
// Key helpers
// ---------------------------------------------------------------------------

const snakeToCamel = (str: string): string =>
  str.replace(/([-_][a-z0-9])/g, g => g.toUpperCase().replace("-", "").replace("_", ""));

// ---------------------------------------------------------------------------
// Batch-result detection
// ---------------------------------------------------------------------------

function isBatchResultList(arr: unknown[]): boolean {
  if (arr.length === 0) return false;
  const first = arr[0] as AnyObject | undefined;
  return typeof first?.success === "boolean" && Boolean(first.result || first.error);
}

/**
 * If a walked batch-result value is a single-key object with a primitive value
 * (i.e. an entity wrapper like `{ invitation: "id" }`), unwrap it to just the value.
 * Multi-key objects (like `{ keyword: "foo", assetsUpdated: 5 }`) pass through unchanged.
 */
function unwrapSingleEntityResult(value: unknown): unknown {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return value;
  const keys = Object.keys(value as AnyObject);
  if (keys.length === 1) {
    const inner = (value as AnyObject)[keys[0]];
    if (inner == null || typeof inner !== "object") return inner;
  }
  return value;
}

// ---------------------------------------------------------------------------
// Flattening — extracts nested entities from a formatted object
// ---------------------------------------------------------------------------

/**
 * Given a formatted entity object and its FlattenConfig, recursively extract
 * nested entities into the shared `entities` bucket. Nested entity values are
 * replaced in-place with their IDs.
 */
function applyFlatten(obj: AnyObject, config: FlattenConfig, entities: EntitiesMap): void {
  for (const [key, target] of Object.entries(config)) {
    if (!(key in obj) || obj[key] == null) continue;

    if (typeof target === "string") {
      // Direct entity reference → extract to the target store
      obj[key] = extractToStore(obj[key], target, entities);
    } else {
      // Sub-config: the property is an array of plain objects whose inner
      // keys should be flattened (e.g. Product.variants[].assets).
      const arr = obj[key];
      if (Array.isArray(arr)) {
        obj[key] = arr.map(item => {
          if (item && typeof item === "object") {
            applyFlatten(item as AnyObject, target, entities);
          }
          return item;
        });
      }
    }
  }
}

/**
 * Extract a single value (or array of values) into the entity store. Returns
 * the ID (or array of IDs) that should replace the original value.
 */
function extractToStore(value: unknown, storeKey: string, entities: EntitiesMap): unknown {
  if (value == null) return value;

  if (Array.isArray(value)) {
    return value.map(v => extractToStore(v, storeKey, entities)).filter(v => v != null);
  }

  // Already an ID (string or number) — nothing to extract
  if (typeof value !== "object") return value;

  const obj = value as AnyObject;
  const storeConfig = storeRegistry[storeKey];
  if (!storeConfig) return value;

  // Recursively flatten this entity's own nested entities first
  if (storeConfig.flatten && Object.keys(storeConfig.flatten).length > 0) {
    applyFlatten(obj, storeConfig.flatten, entities);
  }

  // Store the entity and return its ID
  const id = storeConfig.getId(obj);
  entities[storeKey] ??= {};
  entities[storeKey][id] = obj;

  return id;
}

// ---------------------------------------------------------------------------
// Tree walker — discovers __type-annotated objects in the response
// ---------------------------------------------------------------------------

function processEntity(raw: AnyObject, entry: TypeRegistryEntry, entities: EntitiesMap): unknown {
  // Call the registered formatter (handles internal nesting, camelCase, etc.)
  const formatted = entry.formatter(raw) as AnyObject;

  // Flatten nested entities according to the flatten config
  if (entry.flatten && Object.keys(entry.flatten).length > 0) {
    applyFlatten(formatted, entry.flatten, entities);
  }

  // If no storeKey, keep inline (format-only, don't normalize into entities)
  if (!entry.storeKey) {
    return formatted;
  }

  // Store the entity itself
  const id = entry.getId(formatted);
  entities[entry.storeKey] ??= {};
  entities[entry.storeKey][id] = formatted;

  return id;
}

/**
 * Recursively walk a value from the API response. Entity objects (those with
 * `__type`) are formatted, flattened, and collected into the entities bucket.
 * Plain objects get their keys camelCased. Primitives pass through.
 */
function walk(value: unknown, entities: EntitiesMap): unknown {
  if (value == null || typeof value !== "object") return value;

  if (Array.isArray(value)) {
    // Detect batch-result format: [{ success, result, error }]
    if (isBatchResultList(value)) {
      return value.map(entry => {
        const e = entry as AnyObject;
        const result: AnyObject = { ...e };
        if (e.result != null) {
          result.result = unwrapSingleEntityResult(walk(e.result, entities));
        }
        if (e.error != null && typeof e.error === "object") {
          result.error = walk(e.error, entities);
        }
        return result;
      });
    }
    return value.map(v => walk(v, entities));
  }

  const obj = value as AnyObject;

  // Entity object — format, flatten, store
  if (typeof obj.__type === "string") {
    const entry = typeRegistry[obj.__type as string];
    if (entry) {
      return processEntity(obj, entry, entities);
    }
    // Unknown __type — treat as plain object
  }

  // Plain object — camelCase keys, recurse into values
  const result: AnyObject = {};
  for (const [key, val] of Object.entries(obj)) {
    if (key === "__type") continue;
    result[snakeToCamel(key)] = walk(val, entities);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Format-only walker — same formatting as `walk` but no entity extraction
// ---------------------------------------------------------------------------

function formatWalk(value: unknown): unknown {
  if (value == null || typeof value !== "object") return value;

  if (Array.isArray(value)) {
    if (isBatchResultList(value)) {
      return value.map(entry => {
        const e = entry as AnyObject;
        const result: AnyObject = { ...e };
        if (e.result != null) {
          result.result = formatWalk(e.result);
        }
        if (e.error != null && typeof e.error === "object") {
          result.error = formatWalk(e.error);
        }
        return result;
      });
    }
    return value.map(v => formatWalk(v));
  }

  const obj = value as AnyObject;

  if (typeof obj.__type === "string") {
    const entry = typeRegistry[obj.__type as string];
    if (entry) {
      const formatted = entry.formatter(obj);
      for (const [key, val] of Object.entries(formatted)) {
        formatted[key] = formatWalk(val);
      }
      return formatted;
    }
  }

  const result: AnyObject = {};
  for (const [key, val] of Object.entries(obj)) {
    if (key === "__type") continue;
    result[snakeToCamel(key)] = formatWalk(val);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function autoNormalize<R = unknown>(responseData: unknown): AutoNormalizeResult<R> {
  const entities: EntitiesMap = {};
  const result = walk(responseData, entities);

  if (Object.keys(entities).length > 0) {
    return { entities, result: result as R };
  }
  return { result: result as R };
}

export function autoFormat<R = unknown>(responseData: unknown): { result: R } {
  const result = formatWalk(responseData);
  return { result: result as R };
}
